import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2, Music, FileText, Image as ImageIcon, HardDrive } from "lucide-react";
import {
  listAssets,
  storageQuotaEstimate,
  type OfflineAsset,
} from "@/lib/offline/db";
import { removeAsset } from "@/lib/offline/downloads";

export const Route = createFileRoute("/_authenticated/downloads")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Downloads · KAfrequency" },
      { name: "description", content: "Your offline library — downloaded music, teachings, and activations." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DownloadsPage,
});

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

function iconFor(kind: OfflineAsset["kind"]) {
  if (kind === "audio") return Music;
  if (kind === "image") return ImageIcon;
  return FileText;
}

function DownloadsPage() {
  const [assets, setAssets] = useState<OfflineAsset[]>([]);
  const [quota, setQuota] = useState<{ usage: number; quota: number } | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    const [a, q] = await Promise.all([listAssets(), storageQuotaEstimate()]);
    setAssets(a.sort((x, y) => y.addedAt - x.addedAt));
    setQuota(q);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const totalSize = assets.reduce((s, a) => s + a.size, 0);
  const pct = quota && quota.quota ? Math.min(100, (quota.usage / quota.quota) * 100) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Offline Library</p>
        <h1 className="mt-2 font-serif text-3xl text-gold-luminous sm:text-4xl">Downloads</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Music, teachings, and activations you have saved to this device. Available without a connection.
        </p>
      </header>

      <section className="mb-8 rounded-2xl border hairline bg-charcoal/50 p-4">
        <div className="flex items-center gap-3">
          <HardDrive size={18} className="text-gold" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground">
              {formatBytes(totalSize)} used across {assets.length} item{assets.length === 1 ? "" : "s"}
            </p>
            {quota && (
              <p className="text-xs text-muted-foreground">
                Device storage: {formatBytes(quota.usage)} used of {formatBytes(quota.quota)} available
              </p>
            )}
          </div>
        </div>
        {pct !== null && (
          <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-obsidian">
            <div className="h-full bg-gold-gradient" style={{ width: `${pct}%` }} />
          </div>
        )}
      </section>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : assets.length === 0 ? (
        <div className="rounded-2xl border hairline bg-charcoal/30 p-8 text-center">
          <p className="font-serif text-2xl text-gold-luminous">Nothing saved yet.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tap "Save" on any song, teaching, or activation to make it available offline.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/music"
              className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian"
            >
              Browse music
            </Link>
            <Link
              to="/activations"
              className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous"
            >
              Browse activations
            </Link>
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {assets.map((a) => {
            const Icon = iconFor(a.kind);
            const label = (a.meta?.label as string | undefined) ?? a.key;
            return (
              <li
                key={a.key}
                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border hairline bg-charcoal/40 p-3"
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-obsidian">
                  <Icon size={16} className="text-gold" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm text-foreground">{label}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBytes(a.size)} · {new Date(a.addedAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    await removeAsset(a.key);
                    refresh();
                  }}
                  aria-label={`Remove ${label}`}
                  className="grid h-11 w-11 place-items-center rounded-full text-muted-foreground hover:bg-obsidian hover:text-gold"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
